import { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  startAfter,
} from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/ui/Breadcrumb";

const PAGE_SIZE = 6;
const DEFAULT_THUMB = "/images/default-blog-thumbnail.svg";

export default function BlogsPage() {
  const [posts, setPosts] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const loadingRef = useRef(false);

  const fetchPosts = async (loadMore = false) => {
    if (loadingRef.current) return;
    setLoading(true);
    loadingRef.current = true;

    let q = query(
      collection(db, "blogs"),
      where("status", "==", "published"),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );
    if (loadMore && lastDoc) {
      q = query(
        collection(db, "blogs"),
        where("status", "==", "published"),
        orderBy("createdAt", "desc"),
        startAfter(lastDoc),
        limit(PAGE_SIZE)
      );
    }

    const snap = await getDocs(q);
    const docs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    if (loadMore) {
      setPosts(prev => [...prev, ...docs]);
    } else {
      setPosts(docs);
    }
    setLastDoc(snap.docs[snap.docs.length - 1]);
    setHasMore(snap.docs.length === PAGE_SIZE);
    setLoading(false);
    loadingRef.current = false;
  };

  useEffect(() => {
    fetchPosts(false);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="max-w-6xl mx-auto py-12 px-4">
      <Breadcrumb items={[{ label: t("blog.breadcrumbs") }]} />
      
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.map((post, idx) => (
          <li
            key={post.id || idx}
            className="
              flex flex-col bg-card rounded-2xl border border-border shadow group
              hover:shadow-lg hover:border-primary transition cursor-pointer
              overflow-hidden h-full
            "
            onClick={() => navigate(`/blog/${post.slug}`)}
          >
            {/* Cover Image */}
            <div className="aspect-[16/9] w-full overflow-hidden">
              <img
                src={post.coverImage || DEFAULT_THUMB}
                alt={post.title?.[i18n.language] || post.title?.vi || "Blog"}
                className="w-full h-full object-cover transition group-hover:scale-105 duration-300"
                loading="lazy"
              />
            </div>
            {/* Card content */}
            <div className="flex-1 flex flex-col p-5">
              <h3 className="font-bold text-xl text-primary group-hover:text-secondary mb-2 line-clamp-2 min-h-[56px]">
                {post.title?.[i18n.language] || post.title?.vi}
              </h3>
              <div className="text-text2 text-sm mb-4 line-clamp-3 flex-1">
                {post.excerpt?.[i18n.language] || post.excerpt?.vi}
              </div>
              <div className="mt-auto flex justify-between items-center">
                <span className="text-xs text-gray-400">
                  {post.createdAt?.seconds
                    ? new Date(post.createdAt.seconds * 1000).toLocaleDateString(i18n.language)
                    : ""}
                </span>
                <button
                  className="
                    px-4 py-1 rounded-2xl bg-primary hover:bg-secondary
                    text-textButton font-semibold shadow text-xs transition
                  "
                  onClick={e => { e.stopPropagation(); navigate(`/blog/${post.slug}`); }}
                >
                  {t("blog.readMore")}
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {hasMore && (
        <div className="flex justify-center mt-10">
          <button
            onClick={() => fetchPosts(true)}
            className="px-8 py-3 rounded-2xl bg-primary hover:bg-secondary text-textButton font-bold shadow transition text-base"
            disabled={loading}
          >
            {loading ? t("blog.loading") : t("blog.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}
